import React from "react";
import { Link } from "react-router-dom";
import { HardDrive, Monitor, Server, ArrowRight, ChevronRight, Zap, Shield } from "lucide-react";

/**
 * Migrate — top-level chooser between the three migration kinds.
 * /migrations      → PC capture & apply (workstation replacement)
 * /migrate/server  → Linux/Windows server → server workload move
 * /migrate/storage → NAS / server / folder-level
 */
export default function Migrate() {
    return (
        <div className="mx-auto max-w-6xl p-6">
            <div className="mb-8">
                <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
                    MoovBytes Migrate
                </div>
                <h1 className="mt-1 font-display text-4xl font-bold tracking-tight">
                    Start a new migration
                </h1>
                <p className="mt-1 text-sm text-muted-foreground">
                    Pick the kind of migration. Everything after this is agent-to-agent.
                </p>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                <Card
                    to="/migrations"
                    icon={Monitor}
                    tint="#5b5ef0"
                    eyebrow="Device → Device"
                    title="PC Capture & Apply"
                    sub="Move a whole workstation: profile, Documents, Desktop, Downloads, AppData, apps."
                    bullets={[
                        "Old PC → New PC in minutes",
                        "Windows · macOS · Linux — OS-aware",
                        "Automatic backup of destination user folder",
                    ]}
                    cta="Start computer migration"
                    testId="migrate-chooser-pc"
                />
                <Card
                    to="/migrate/server"
                    icon={Server}
                    tint="#7c5cff"
                    eyebrow="Server → Server"
                    title="Server Migration"
                    sub="Lift-and-shift Linux or Windows Server workloads — config, services, data, certs."
                    bullets={[
                        "Web · database · application · infrastructure roles",
                        "Preserve permissions, systemd units, SSL certs",
                        "Optional stop-service window for clean cutover",
                    ]}
                    cta="Start server migration"
                    testId="migrate-chooser-server"
                />
                <Card
                    to="/migrate/storage"
                    icon={HardDrive}
                    tint="#00C2FF"
                    eyebrow="Server / NAS / Cloud"
                    title="Data Migration"
                    sub="Move data between NAS, Azure Blob Storage, and direct PC/server storage — connect by agent or IP/path, browse into any folder."
                    bullets={[
                        "Continuous sync or one-time copy",
                        "Mirror deletes, bandwidth caps, versioning",
                        "Synology · QNAP · NetApp · Azure Blob · any SMB or NFS",
                    ]}
                    cta="Start data migration"
                    testId="migrate-chooser-storage"
                />
            </div>

            <div className="mt-8 flex items-center justify-center gap-6 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                <span className="inline-flex items-center gap-1.5"><Zap size={11} /> Delta block sync</span>
                <span className="inline-flex items-center gap-1.5"><Shield size={11} /> AES-256-GCM</span>
                <span className="inline-flex items-center gap-1.5"><ArrowRight size={11} /> Never through control plane</span>
            </div>
        </div>
    );
}

function Card({ to, icon: Icon, tint, eyebrow, title, sub, bullets, cta, testId }) {
    return (
        <Link
            to={to}
            data-testid={testId}
            className="group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-surface p-6 transition-all hover:-translate-y-0.5 hover:shadow-2xl"
            style={{ "--tint": tint }}
        >
            <div
                className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full opacity-20 blur-3xl transition-opacity group-hover:opacity-40"
                style={{ background: tint }}
                aria-hidden
            />
            <div
                className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl shadow-sm"
                style={{ background: `${tint}22`, color: tint }}
            >
                <Icon size={22} />
            </div>
            <div
                className="mb-1 font-mono text-[10px] font-semibold uppercase tracking-[0.22em]"
                style={{ color: tint }}
            >
                {eyebrow}
            </div>
            <div className="font-display text-2xl font-bold">{title}</div>
            <p className="mt-1 text-sm text-muted-foreground">{sub}</p>
            <ul className="mt-4 space-y-1.5 text-[12px] text-muted-foreground">
                {bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                        <ChevronRight size={12} className="mt-0.5 shrink-0" style={{ color: tint }} />
                        <span>{b}</span>
                    </li>
                ))}
            </ul>
            <div className="mt-6 flex items-center justify-between">
                <span
                    className="inline-flex items-center gap-1.5 font-mono text-[11px] font-semibold uppercase tracking-widest"
                    style={{ color: tint }}
                >
                    {cta} <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" />
                </span>
            </div>
        </Link>
    );
}
